import React, { useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { api, API, apiError } from "../lib/api";
import { Logo } from "../components/Logo";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {randomId} from "../lib/randomId";

const RECEIVED = "Richiesta ricevuta. Se l’account è attivo, controlla la posta, anche nella cartella spam. Se il messaggio non arriva, riprova o contatta l’amministratore.";
const RECOVERY_KEY = `autotu:password-recovery:v1:${API}`;
const pendingRecoveryKey = () => {try{const key=sessionStorage.getItem(RECOVERY_KEY)||'';return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(key)?key:'';}catch{return '';}};
const FORGOT_PENDING = 'L’esito della richiesta è da verificare. Ripeti esplicitamente la stessa richiesta con la stessa email.';
const RESET_PENDING = 'L’esito non è ancora confermato. Verifica accedendo con la nuova password; puoi ripetere la stessa richiesta.';

function RecoveryFrame({ title, description, children }) {
  return <main className="flex min-h-screen items-center justify-center bg-background p-4">
    <section className="w-full max-w-md rounded-2xl border border-border bg-card p-6 sm:p-10" aria-labelledby="recovery-title">
      <div className="mb-8"><Logo /></div>
      <h1 id="recovery-title" className="font-heading text-2xl font-bold tracking-tight">{title}</h1>
      <p className="mb-6 mt-2 text-sm leading-relaxed text-muted-foreground">{description}</p>
      {children}
      <Link to="/login" className="mt-6 inline-flex min-h-11 items-center text-sm font-medium text-primary underline-offset-4 hover:underline">Torna all’accesso</Link>
    </section>
  </main>;
}

export function RequestPasswordReset() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [received, setReceived] = useState(false);
  const [uncertain,setUncertain]=useState(()=>!!pendingRecoveryKey()),[conflict,setConflict]=useState(false);
  const operationKey=useRef(pendingRecoveryKey()),submittedEmail=useRef(null);
  const pending = useRef(false);

  async function submit(event) {
    event.preventDefault();
    if (pending.current) return;
    pending.current = true; setBusy(true); setError(""); setReceived(false);
    try {
      const normalized=email.trim().toLowerCase();
      if(submittedEmail.current&&submittedEmail.current!==normalized){setError('Per verificare questa richiesta usa la stessa email.');return;}
      if(!operationKey.current)operationKey.current=randomId();
      sessionStorage.setItem(RECOVERY_KEY,operationKey.current);
      if(sessionStorage.getItem(RECOVERY_KEY)!==operationKey.current)throw new Error('Storage unavailable');
      submittedEmail.current=normalized;
      const response=await api.post("/auth/forgot-password", { email: normalized },{headers:{'Idempotency-Key':operationKey.current}});
      if(response.status===202||response.data?.state==='pending'){setUncertain(true);return;}
      setUncertain(false);sessionStorage.removeItem(RECOVERY_KEY);operationKey.current='';submittedEmail.current=null;
      setReceived(true);
    } catch (failure) {
      setUncertain(true);
      const detail=failure.response?.data?.detail;
      setConflict(failure.response?.status===409&&(detail==='OPERATION_KEY_CONFLICT'||detail?.code==='OPERATION_KEY_CONFLICT'));
      setError(apiError(failure));
    } finally {
      pending.current = false; setBusy(false);
    }
  }

  return <RecoveryFrame title="Recupera la password" description="Inserisci l’email del tuo account per richiedere il collegamento di recupero.">
    <form onSubmit={submit} className="space-y-4" aria-busy={busy}>
      <div>
        <Label htmlFor="recovery-email" className="mb-1.5 block">Email dell’account</Label>
        <Input id="recovery-email" type="email" autoComplete="email" required maxLength={254}
          value={email} disabled={busy} onChange={event => setEmail(event.target.value)}
          aria-describedby={error ? "recovery-error" : undefined} />
      </div>
      {error && <p id="recovery-error" role="alert" className="text-sm leading-relaxed text-destructive">{error}</p>}
      {received && <p role="status" className="text-sm leading-relaxed">{RECEIVED}</p>}
      {uncertain&&<p role="status" className="text-sm leading-relaxed">{FORGOT_PENDING}</p>}
      <Button type="submit" disabled={busy} className="min-h-11 w-full">{busy ? "Invio richiesta…" : uncertain?"Ripeti la stessa richiesta":"Invia istruzioni"}</Button>
      {conflict&&<Button type="button" variant="outline" disabled={busy} className="min-h-11 w-full" onClick={()=>{operationKey.current='';submittedEmail.current=null;sessionStorage.removeItem(RECOVERY_KEY);setConflict(false);setUncertain(false);setError('');}}>Prepara una nuova richiesta per questa email</Button>}
    </form>
  </RecoveryFrame>;
}

export function ResetPassword() {
  const location = useLocation();
  const navigate = useNavigate();
  const token = new URLSearchParams(location.search).get("token") || "";
  const validToken = /^[A-Za-z0-9_-]{20,200}$/.test(token);
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [completed, setCompleted] = useState(false);
  const [uncertain,setUncertain]=useState(false);
  const pending = useRef(false);

  function passwordError() {
    if (Array.from(password).length < 12 || Array.from(password).length > 72) return "Usa una password da 12 a 72 caratteri.";
    if (new Blob([password]).size > 72) return "La password supera 72 byte: riduci i caratteri, soprattutto quelli accentati o i simboli.";
    if (password !== confirmation) return "Le due password non coincidono.";
    return "";
  }

  async function submit(event) {
    event.preventDefault();
    if (pending.current || !validToken) return;
    const validation = passwordError();
    if (validation) { setError(validation); return; }
    pending.current = true; setBusy(true); setError("");
    try {
      const response=await api.post("/auth/reset-password", { token, password });
      if(response.status===202||response.data?.state==='pending'){setUncertain(true);return;}
      setPassword(""); setConfirmation(""); setCompleted(true);
      navigate("/reset-password", { replace: true });
    } catch (failure) {
      if(!failure.response || failure.response.status>=500)setUncertain(true);
      setError(apiError(failure));
    } finally {
      pending.current = false; setBusy(false);
    }
  }

  return <RecoveryFrame title="Scegli una nuova password" description="Il collegamento può essere utilizzato una sola volta e scade dopo un’ora.">
    {completed ? <p role="status" className="text-sm leading-relaxed">Password aggiornata. Accedi con la nuova password; le sessioni precedenti sono state terminate.</p> : !validToken ?
      <p role="alert" className="text-sm leading-relaxed text-destructive">Il collegamento di recupero è assente o non valido. Richiedine uno nuovo.</p> :
      <form onSubmit={submit} className="space-y-4" aria-busy={busy}>
        <div>
          <Label htmlFor="new-password" className="mb-1.5 block">Nuova password</Label>
          <Input id="new-password" type={visible ? "text" : "password"} autoComplete="new-password"
            required minLength={12} maxLength={72} disabled={busy||uncertain} value={password}
            onChange={event => setPassword(event.target.value)} aria-describedby="password-help" />
          <p id="password-help" className="mt-2 text-xs leading-relaxed text-muted-foreground">Da 12 a 72 caratteri, entro 72 byte. I caratteri accentati e alcuni simboli occupano più byte.</p>
        </div>
        <div>
          <Label htmlFor="confirm-password" className="mb-1.5 block">Conferma la nuova password</Label>
          <Input id="confirm-password" type={visible ? "text" : "password"} autoComplete="new-password"
            required minLength={12} maxLength={72} disabled={busy||uncertain} value={confirmation}
            onChange={event => setConfirmation(event.target.value)}
            onBlur={() => { if (password && confirmation) setError(passwordError()); }}
            aria-describedby={error ? "reset-error" : undefined} />
        </div>
        <button type="button" disabled={busy} onClick={() => setVisible(value => !value)} aria-pressed={visible}
          className="inline-flex min-h-11 items-center text-sm font-medium text-primary underline-offset-4 hover:underline">
          {visible ? "Nascondi password" : "Mostra password"}
        </button>
        {error && <p id="reset-error" role="alert" className="text-sm leading-relaxed text-destructive">{error}</p>}
        {uncertain&&<p role="status" className="text-sm leading-relaxed">{RESET_PENDING}</p>}
        <Button type="submit" disabled={busy} className="min-h-11 w-full">{busy ? "Salvataggio…" : uncertain?"Ripeti la stessa richiesta":"Salva nuova password"}</Button>
      </form>}
    {!completed && <Link to="/forgot-password" className="mt-4 inline-flex min-h-11 items-center text-sm font-medium text-primary underline-offset-4 hover:underline">Richiedi un nuovo collegamento</Link>}
  </RecoveryFrame>;
}
